import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PipeOption {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  renderIcon?: () => ReactNode;
  count?: number;
  active: boolean;
  disabled?: boolean;
  iconTone?: 'accent' | 'green' | 'indigo' | 'muted';
}

interface Props {
  pipes: PipeOption[];
  selected: string | null;
  onSelect: (pipeId: string) => void;
}

// Usa classes legacy diretas (.pipe-sel, .pipe-sel-icon, .pipe-sel-title, .pipe-sel-badge)
// definidas em web/src/ui/legacy.css. Garante paridade visual exata com o app v7.
export function PipeSelector({ pipes, selected, onSelect }: Props) {
  return (
    <div className="pipe-grid !px-0">
      {pipes.map((p) => {
        const Icon = p.icon;
        const tone = p.iconTone ?? 'accent';
        const isSelected = selected === p.id;

        const iconEl = p.renderIcon ? (
          p.renderIcon()
        ) : (
          <span className={cn('pipe-sel-icon', `pipe-sel-icon--${tone}`)}>
            <Icon size={20} strokeWidth={1.5} />
          </span>
        );

        if (p.disabled) {
          return (
            <div key={p.id} className="pipe-sel-disabled">
              {iconEl}
              <span className="pipe-sel-title pipe-sel-title--muted">{p.label}</span>
              <span className="pipe-sel-coming-soon">Em breve</span>
            </div>
          );
        }

        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className={cn('pipe-sel', isSelected && 'border-accent')}
            style={isSelected ? { borderColor: 'var(--accent)' } : undefined}
          >
            {iconEl}
            <span className="pipe-sel-title">{p.label}</span>
            {p.description && <span className="pipe-sel-sub">{p.description}</span>}
            {p.count != null && p.count > 0 && (
              <span className={cn('pipe-sel-badge', `pipe-sel-badge--${tone}`)}>
                {p.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
